import { useEffect, useRef, useState } from "react";
import { siteData } from "@/store/siteData";
import { useImageUrl } from "@/hooks/useImageUrl";

function FeatureCard({ feat, index, visible }: { feat: ReturnType<typeof siteData.getFeatures>[0]; index: number; visible: boolean }) {
  const imgUrl = useImageUrl(feat.imageId, feat.imageUrl);
  return (
    <div className={`group relative overflow-hidden border border-[#E2E8F0] hover:border-[#3B82F6] transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{ transitionDelay: `${index * 150}ms` }}>
      <div className="relative h-56 overflow-hidden">
        <img src={imgUrl} alt={feat.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#F1F5F9] via-[#F1F5F9]/30 to-transparent"></div>
        <div className="absolute top-5 left-5 w-10 h-10 flex items-center justify-center border border-[#3B82F6] bg-[#F1F5F9]/80">
          <i className={`${feat.icon} text-[#334155] text-lg`}></i>
        </div>
      </div>
      <div className="p-6 bg-[#FFFFFF]">
        <h3 className="text-[#0F172A] font-bold text-xl mb-3 uppercase tracking-wide">{feat.title}</h3>
        <p className="text-[#64748B] text-sm leading-relaxed">{feat.desc}</p>
      </div>
    </div>
  );
}

export default function FeaturesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const features = siteData.getFeatures();
  const settings = siteData.getGeneralSettings();

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} data-edit="features" className="bg-[#F1F5F9] py-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className={`mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-[#3B82F6]"></div>
            <span className="text-[#334155] text-xs font-semibold tracking-[0.3em] uppercase">Neden {settings.companyName}?</span>
          </div>
          <h2 className="text-[#0F172A] font-black uppercase leading-tight" style={{ fontSize: "clamp(20px, 2.8vw, 34px)", letterSpacing: "-0.02em" }}>
            Güç, Güven ve <span className="text-[#64748B]">Teknoloji Bir Arada</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((feat, i) => <FeatureCard key={feat.id} feat={feat} index={i} visible={visible} />)}
        </div>
      </div>
    </section>
  );
}
